import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Element } from 'react-scroll'
import { Car, Driver } from '~/shared/api'
import { debtTypeEnumOptions } from '~/shared/config/constants'
import { TMS_DOMAIN } from '~/shared/config/env'
import { LinkDecorator } from '~/shared/ui/Buttons'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  DatePickerInput,
  FormButtons,
  formButtonsModel,
  FormProvider,
  NumericInput,
  TextareaInput,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { formSchema, FormValues } from './model'

type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
  canCarEdit?: boolean
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
  canCarEdit,
}: FormProps) {
  const { t } = useTranslation()
  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = !isCreating || isSubmitting
  const isCarDisabled = canCarEdit ? !isEditing : !isCreating

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Element name='main'>
        <Container>
          <ContainerTitle>{t('basic information')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={6}>
              <AsyncAutocompleteInput
                label='form.driver'
                name='driverOption'
                fetchOptions={Driver.fetchOptions}
                readOnly={disabled}
                placeholder='form.select'
                skeletonShow={isLoading}
                endDecorator={
                  disabled &&
                  defaultValues?.driverExtId && (
                    <LinkDecorator
                      tooltipText={t('form.go to driver card')}
                      href={`${TMS_DOMAIN}drivers/${defaultValues?.driverExtId}`}
                    />
                  )
                }
              />
            </Grid>
            <Grid xs={6}>
              <AutocompleteInput
                label='form.type'
                name='sourceType'
                options={debtTypeEnumOptions}
                readOnly={disabled}
                placeholder='form.select'
                skeletonShow={isLoading}
              />
            </Grid>
            <Grid xs={6}>
              <AsyncAutocompleteInput
                label='form.car'
                name='carOption'
                fetchOptions={Car.fetchOptions}
                placeholder='form.select'
                skeletonShow={isLoading}
                readOnly={isCarDisabled || isSubmitting}
                endDecorator={
                  isCarDisabled &&
                  defaultValues?.driverExtId && (
                    <LinkDecorator
                      tooltipText={t('form.go to car card')}
                      href={`${TMS_DOMAIN}cars/${defaultValues?.carExtId}`}
                    />
                  )
                }
              />
            </Grid>
            {!isCreating && (
              <>
                <Grid xs={6}>
                  <TextInput
                    label='form.base'
                    name='sourceTitle'
                    readOnly
                    skeletonShow={isLoading}
                    endDecorator={
                      defaultValues?.sourceUrl && (
                        <LinkDecorator
                          tooltipText='form.go'
                          href={defaultValues?.sourceUrl}
                        />
                      )
                    }
                  />
                </Grid>
                <Grid xs={6}>
                  <DatePickerInput
                    label='form.write-off start date'
                    name='startAt'
                    readOnly
                    skeletonShow={isLoading}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Container>
      </Element>

      <Element name='payment'>
        <Container>
          <ContainerTitle>{t('payment')}</ContainerTitle>
          <Grid container spacing={2.5} alignItems='flex-start'>
            <Grid xs={6} container spacing={2.5}>
              <Grid xs={6}>
                <NumericInput
                  label='form.total'
                  name='total'
                  placeholder='form.enter total'
                  skeletonShow={isLoading}
                  readOnly={(!isCreating && !isEditing) || isSubmitting}
                />
              </Grid>
              <Grid xs={6}>
                <NumericInput
                  label='form.payment cost'
                  name='paymentCost'
                  placeholder='form.enter total'
                  skeletonShow={isLoading}
                  readOnly={(!isCreating && !isEditing) || isSubmitting}
                />
              </Grid>
              <Grid xs={6}>
                <NumericInput
                  label='form.paid'
                  name='paid'
                  readOnly
                  skeletonShow={isLoading}
                />
              </Grid>
              <Grid xs={6}>
                <NumericInput
                  label='form.left amount'
                  name='leftAmount'
                  readOnly
                  skeletonShow={isLoading}
                />
              </Grid>
            </Grid>
            <Grid xs={6}>
              <TextareaInput
                label='form.comment'
                name='comment'
                placeholder='form.enter comment'
                minRows={4}
                maxRows={4}
                skeletonShow={isLoading}
                readOnly={(!isCreating && !isEditing) || isSubmitting}
                sx={{
                  height: '138px',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
